/**
 * The rejection report form, sheet 23 callouts 2, 3, 5, 6, 8 and 9 (mission 6
 * WP10 T053; FR-021, FR-023, FR-024, FR-027; ANA.11, T.3, X.1, X.9, GEN.4,
 * X.10; C-006, C-010, C-013; DM-01M39PR5FSZDGG214WPRBW9ZXD; research §1.22;
 * post-plan S12, S13).
 *
 * Above the fields: register 14's purpose sentence (the fallback when the
 * shared `['rules']` read fails), the read-only run context, and the ANA.4
 * legend, which the dialog's focus trap would otherwise put out of reach.
 *
 * The rejection text is restricted customer free text (CLAUDE.md §3.2). It
 * lives in this component's state and nowhere else: no `useMutation` (its
 * `variables` would keep it in the mutation cache), no URL, no storage, no
 * log, no attribute. The text area sets `spellCheck={false}` and
 * `autoComplete="off"` (S12) and is bounded by `REJECTION_TEXT_MAX` (C-010).
 * The text is cleared on a 201, and the form unmounts on close.
 *
 * Client validation is structural only: a bureau must be chosen, the date is
 * bounded by today in UTC, the length by the limit, and a whitespace-only text
 * is not sent. Every per-field message is the API's own (`errors[].message`).
 * The wrapper builds the body key by key (C-006). No attachment (C-013).
 *
 * Mission 7 WP11 (T061; FR-017; sheet 23 callouts 4, 5 and 9; ANA.11, TX.6;
 * research §1.16; OI-4; squad U17): the form reads the run's transmissions
 * (`runTransmissionsQuery`, its first page) and keeps those whose result is
 * `success` or `timeout`, the only attempts that may have delivered.
 * - With one or more, a `deliveryChoiceLegend` fieldset offers one native radio
 *   per transmission plus `deliveredByHand`. **Nothing is selected** unless
 *   `?transmission=<id>` names one of them (U17). Choosing a transmission sets
 *   the bureau to its bureau and locks it (`prefilledFromTransmission`); Submit
 *   then sends its `transmission_id` and `delivered_myself: false`. Submit with
 *   no choice made sends nothing and moves focus to the first radio.
 * - With none, or while the read is in flight, delivery stays fixed to
 *   "delivered by hand" as mission 6 built it. Submit waits for the
 *   read to settle, so a report is never filed as hand delivery by accident.
 * - The server's `validation-failed` on `transmission_id` renders under the
 *   choice, and on `bureau` under the bureau.
 * The rejection text never enters the query cache or any URL; the `?transmission=`
 * param carries an id only.
 *
 * Mission 8 WP11 (T058; FR-022; mission 7 review follow-up 10, UI-3, and the
 * "Frontend residuals" row; ANA.11, TX.6): the read is `runTransmissionsAll`,
 * walked page by page to the cursor's end, so an attempt beyond the first 50 is
 * offered; the choice renders once every page has answered. A failed read (the
 * first page or a later one) replaces the delivery line with a blocked
 * `transmissionsReadFailed` band with Try again and disables Submit with that
 * reason: a report is never filed as hand delivery because the read failed.
 */
import { Fragment, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { CircleAlert } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogBody, DialogClose, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { SeverityBadge } from '@/components/trellis/SeverityBadge'
import { SeverityLegend } from '@/components/trellis/SeverityLegend'
import { useTenant } from '@/components/trellis/AuthGate'
import { ApiError, type Problem } from '@/lib/api'
import { BUREAUS, type Bureau } from '@/lib/api/headerParams'
import { REJECTION_TEXT_MAX, reportRejection } from '@/lib/api/rejection'
import type { Run } from '@/lib/api/runs'
import type { TransmissionEntry } from '@/lib/api/transmissions'
import { Mono, count, date, isoDateTime, time } from '@/lib/format'
import { PROBLEM, type ProblemOverrides } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { rulesQuery, runTransmissionsAll } from '@/lib/queries'
import { SEVERITY_ORDER, type Severity } from '@/lib/severity'

/** The bureau names as US10 writes them. */
const BUREAU_LABELS: Record<Bureau, string> = {
  equifax: 'Equifax',
  experian: 'Experian',
  transunion: 'TransUnion',
  innovis: 'Innovis',
}

/** The fields a 400 `validation-failed` can name that own a control here. */
const FIELDS = [
  'bureau',
  'rejected_on',
  'rejection_text',
  'delivered_myself',
  'transmission_id',
  'contact_consent',
  'altered_after_download',
] as const
type Field = (typeof FIELDS)[number]

function isField(value: string): value is Field {
  return (FIELDS as readonly string[]).includes(value)
}

/**
 * This screen's renderings (contracts/ui-api.md, sheet 23): `run-not-ready`
 * is the UI's own sentence, never the API's download wording; X.10 names why
 * a file Trellis did not generate cannot carry a report.
 */
export const REJECTION_OVERRIDES: ProblemOverrides = {
  'run-not-ready': { band: 'info', remedy: 'rejectionRunNotReady' },
  // mission 6 WP13: handoff from WP10: sheet 23's own key, no longer sheet 30's pathBNotGenerated.
  'run-not-transmissible': { band: 'blocked', remedy: 'rejectionPathBNotGenerated' },
}

/** The X.10 refusal, built without a request, for a Path B run (C11). */
export const NOT_TRANSMISSIBLE: Problem = {
  type: PROBLEM.RUN_NOT_TRANSMISSIBLE,
  title: 'run-not-transmissible',
  status: 409,
  requirement: 'X.10',
}

type Failure = { kind: 'problem'; problem: Problem } | { kind: 'busy'; detail: string } | { kind: 'generic' }

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10)
}

function total(run: Run, severity: Severity): number {
  if (severity === 'ERROR') return run.error_count ?? 0
  if (severity === 'WARN') return run.warn_count ?? 0
  return run.info_count ?? 0
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="contents">
      <dt className="text-caption text-navy-600">{label}</dt>
      <dd className="min-w-0 text-body-sm text-navy-950">{children}</dd>
    </div>
  )
}

/** Callout 23/3: what Bloom needs to reproduce the findings. Read-only. */
function RunContext({ run }: { run: Run }) {
  return (
    <dl data-run-context="" className="grid grid-cols-[auto_1fr] items-baseline gap-x-4 gap-y-2">
      {/* mission 6 WP13: handoff from WP10: the 23/3 fact labels come from the register (fact.*). */}
      <Fact label={copyText('fact.runId')}>
        <Mono className="break-all">{run.run_id}</Mono>
      </Fact>
      {run.upload ? (
        <Fact label={copyText('fact.input')}>
          <Mono className="break-all">{run.upload.filename}</Mono>
        </Fact>
      ) : null}
      {run.layout_version ? (
        <Fact label={copyText('fact.layout')}>
          <Mono>{run.layout_version}</Mono>
        </Fact>
      ) : null}
      <Fact label={copyText('fact.ruleSet')}>
        <Mono>{run.rule_set_version}</Mono>
      </Fact>
      <Fact label={copyText('findingsColumn')}>
        <span className="flex flex-wrap items-center gap-4">
          {SEVERITY_ORDER.map((severity) => (
            <span key={severity} className="inline-flex items-center gap-1.5">
              <SeverityBadge severity={severity} />
              <Mono>{count(total(run, severity))}</Mono>
            </span>
          ))}
        </span>
      </Fact>
    </dl>
  )
}

/** A private-use mark the purpose sentence is rendered around, so its count can sit in mono. */
const COUNT_MARK = '\uE000'

/**
 * Callout 23/2: register 14's purpose with the implemented-rule count from the
 * shared `['rules']` read (US10.1; research §1.22), the count in mono
 * (CLAUDE.md §5). The fallback sentence when the read fails; a skeleton while
 * it loads.
 */
function Purpose() {
  const rules = useQuery(rulesQuery())
  if (rules.isPending) {
    return (
      <div data-purpose="">
        <Skeleton className="h-4 w-full" />
      </div>
    )
  }
  if (rules.isError) {
    return (
      <div data-purpose="">
        <p className="text-body text-navy-950">{copyText('rejectionPurposeFallback')}</p>
      </div>
    )
  }
  const parts = PROVISIONAL_COPY.rejectionPurposeWithCount.render({ ruleCount: COUNT_MARK }).split(COUNT_MARK)
  return (
    <div data-purpose="">
      <p className="text-body text-navy-950">
        {parts.map((part, i) => (
          <Fragment key={i}>
            {i > 0 ? <Mono>{count(rules.data.count)}</Mono> : null}
            {part}
          </Fragment>
        ))}
      </p>
    </div>
  )
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p id={id} data-field-error="" className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
      <CircleAlert aria-hidden className="size-4 shrink-0" />
      {message}
    </p>
  )
}

/** The choice value for hand delivery; a transmission is chosen by its id. */
const BY_HAND = 'by-hand'

/** Only these attempts may have delivered the file (OI-4; research §1.16). */
function mayHaveDelivered(entry: TransmissionEntry): boolean {
  return entry.result === 'success' || entry.result === 'timeout'
}

const TIME_MARK = '\uE001'
const FILE_MARK = '\uE002'

/** Register 31's choice sentence, its time in `<time dateTime>` and its filename in mono. */
function TransmissionChoiceLabel({ entry }: { entry: TransmissionEntry }) {
  const text = PROVISIONAL_COPY.deliveryChoiceTransmission.render({
    time: TIME_MARK,
    bureau: entry.bureau,
    filename: FILE_MARK,
    result: entry.result,
  })
  return (
    <>
      {text.split(/(\uE001|\uE002)/).map((part, i) => {
        if (part === TIME_MARK) {
          return (
            <time key={i} dateTime={isoDateTime(entry.attempted_at)}>
              {date(entry.attempted_at)} {time(entry.attempted_at)}
            </time>
          )
        }
        if (part === FILE_MARK) return <Mono key={i}>{entry.remote_filename}</Mono>
        return <Fragment key={i}>{part}</Fragment>
      })}
    </>
  )
}

const RADIO =
  'mt-0.5 size-[18px] shrink-0 cursor-pointer appearance-none rounded-full border-[1.5px] border-navy-500 bg-white ' +
  'checked:border-[var(--color-primary)] checked:bg-[radial-gradient(circle,var(--color-primary)_3px,transparent_3.5px)]'

export interface RejectionFormProps {
  run: Run
  onSubmitted(reportNumber: string): void
}

export function RejectionForm({ run, onSubmitted }: RejectionFormProps) {
  const tenant = useTenant()
  const [bureau, setBureau] = useState<Bureau | ''>('')
  const [rejectedOn, setRejectedOn] = useState('')
  const [text, setText] = useState('')
  const [consent, setConsent] = useState(false)
  const [altered, setAltered] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({})
  const [failure, setFailure] = useState<Failure | null>(null)
  const inFlight = useRef(false)
  const textRef = useRef<HTMLTextAreaElement>(null)
  const firstChoiceRef = useRef<HTMLInputElement>(null)

  const [params] = useSearchParams()
  const transmissions = useInfiniteQuery(runTransmissionsAll(run.run_id))
  const { hasNextPage, isFetchingNextPage, isError: readFailed, fetchNextPage } = transmissions
  // Walk to the cursor's end (UI-3); a failed page stops the walk until Try again.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !readFailed) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, readFailed, fetchNextPage])
  const settled = transmissions.isSuccess && !hasNextPage
  const offered = settled ? transmissions.data.pages.flatMap((page) => page.entries).filter(mayHaveDelivered) : []
  const requested = params.get('transmission')
  const preselected = offered.some((t) => t.transmission_id === requested) ? requested : null
  const [picked, setPicked] = useState<string | null>(null)
  const delivery = picked ?? preselected
  const chosen = offered.find((t) => t.transmission_id === delivery)
  const reportBureau: Bureau | '' = chosen ? chosen.bureau : bureau

  const base = useId()
  const id = (part: string) => `${base}-${part}`
  const errorId = (field: Field) => (errors[field] === undefined ? undefined : id(`${field}-error`))
  const invalid = (field: Field) => (errors[field] === undefined ? undefined : true)
  const describedBy = (...ids: Array<string | undefined>) => ids.filter(Boolean).join(' ') || undefined

  function answer(err: unknown) {
    if (!(err instanceof ApiError)) {
      setFailure({ kind: 'generic' })
      return
    }
    const problem = err.problem
    if (problem.type === PROBLEM.VALIDATION_FAILED && problem.errors && problem.errors.length > 0) {
      const mapped: Partial<Record<Field, string>> = {}
      let unmapped = false
      for (const e of problem.errors) {
        if (isField(e.field)) mapped[e.field] ??= e.message
        else unmapped = true
      }
      setErrors(mapped)
      if (unmapped) setFailure({ kind: 'problem', problem })
      return
    }
    if (problem.type === PROBLEM.TRY_AGAIN && problem.status === 503) {
      setFailure({ kind: 'busy', detail: problem.detail ?? copyText('genericError') })
      return
    }
    setFailure({ kind: 'problem', problem })
  }

  async function submit() {
    if (inFlight.current || reportBureau === '' || !settled) return
    if (offered.length > 0 && delivery === null) {
      firstChoiceRef.current?.focus()
      return
    }
    if (text.trim() === '') {
      textRef.current?.focus()
      return
    }
    inFlight.current = true
    setSubmitting(true)
    setErrors({})
    setFailure(null)
    try {
      const report = await reportRejection(run.run_id, {
        bureau: reportBureau,
        rejected_on: rejectedOn,
        rejection_text: text,
        contact_consent: consent,
        altered_after_download: altered,
        ...(chosen ? { transmission_id: chosen.transmission_id } : {}),
      })
      setText('')
      onSubmitted(report.report_number)
    } catch (err) {
      answer(err)
      setSubmitting(false)
    } finally {
      inFlight.current = false
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  const deliveryError = errors.transmission_id ?? errors.delivered_myself
  const email = tenant.data?.contact_email ?? ''
  const length = [...text.trim()].length

  return (
    <form noValidate onSubmit={onSubmit} className="flex min-h-0 flex-col">
      <DialogBody className="flex flex-col gap-5">
        <Purpose />
        <RunContext run={run} />
        <SeverityLegend />

        {failure?.kind === 'problem' ? <ProblemBand problem={failure.problem} override={REJECTION_OVERRIDES} /> : null}
        {failure?.kind === 'busy' ? (
          <Band
            kind="info"
            action={
              <Button variant="secondary" size="sm" onClick={() => void submit()}>
                Retry
              </Button>
            }
          >
            <p>{failure.detail}</p>
          </Band>
        ) : null}
        {failure?.kind === 'generic' ? (
          <Band kind="blocked">
            <p>{copyText('genericError')}</p>
          </Band>
        ) : null}

        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={id('bureau')}>Bureau</Label>
            <Select value={reportBureau} onValueChange={(value) => setBureau(value as Bureau)} disabled={chosen !== undefined}>
              <SelectTrigger
                id={id('bureau')}
                aria-invalid={invalid('bureau')}
                aria-describedby={describedBy(chosen ? id('prefilled') : undefined, errorId('bureau'))}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUREAUS.map((b) => (
                  <SelectItem key={b} value={b}>
                    {BUREAU_LABELS[b]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {chosen ? (
              <p id={id('prefilled')} className="text-caption text-navy-600">
                {copyText('prefilledFromTransmission')}
              </p>
            ) : null}
            {errors.bureau !== undefined ? <FieldError id={id('bureau-error')} message={errors.bureau} /> : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={id('rejected_on')}>Rejection date</Label>
            <Input
              id={id('rejected_on')}
              type="date"
              max={todayUtc()}
              value={rejectedOn}
              onChange={(e) => setRejectedOn(e.target.value)}
              aria-invalid={invalid('rejected_on')}
              aria-describedby={errorId('rejected_on')}
              className="font-mono"
            />
            {errors.rejected_on !== undefined ? (
              <FieldError id={id('rejected_on-error')} message={errors.rejected_on} />
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={id('rejection_text')}>Rejection text</Label>
          <Textarea
            ref={textRef}
            id={id('rejection_text')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            autoComplete="off"
            maxLength={REJECTION_TEXT_MAX}
            rows={6}
            aria-invalid={invalid('rejection_text')}
            aria-describedby={describedBy(id('helper'), id('counter'), errorId('rejection_text'))}
          />
          <div className="flex items-start justify-between gap-4">
            <p id={id('helper')} className="text-caption text-navy-600">
              {copyText('rejectionHelper')}
            </p>
            <p id={id('counter')} data-counter="" className="shrink-0 text-caption text-navy-600">
              <Mono>{count(length)}</Mono> / <Mono>{count(REJECTION_TEXT_MAX)}</Mono>
            </p>
          </div>
          {errors.rejection_text !== undefined ? (
            <FieldError id={id('rejection_text-error')} message={errors.rejection_text} />
          ) : null}
        </div>

        {offered.length > 0 ? (
          <fieldset
            className="flex flex-col gap-2"
            aria-invalid={deliveryError === undefined ? undefined : true}
            aria-describedby={deliveryError === undefined ? undefined : id('delivery-error')}
          >
            <legend className="mb-1 text-body-sm font-medium text-navy-950">{copyText('deliveryChoiceLegend')}</legend>
            {offered.map((entry, i) => (
              <label key={entry.transmission_id} className="flex cursor-pointer items-start gap-2 text-body text-navy-950">
                <input
                  ref={i === 0 ? firstChoiceRef : undefined}
                  type="radio"
                  name={id('delivery')}
                  value={entry.transmission_id}
                  checked={delivery === entry.transmission_id}
                  onChange={() => setPicked(entry.transmission_id)}
                  className={RADIO}
                />
                <span>
                  <TransmissionChoiceLabel entry={entry} />
                </span>
              </label>
            ))}
            <label className="flex cursor-pointer items-start gap-2 text-body text-navy-950">
              <input
                type="radio"
                name={id('delivery')}
                value={BY_HAND}
                checked={delivery === BY_HAND}
                onChange={() => setPicked(BY_HAND)}
                className={RADIO}
              />
              <span>{copyText('deliveredByHand')}</span>
            </label>
            {deliveryError !== undefined ? <FieldError id={id('delivery-error')} message={deliveryError} /> : null}
          </fieldset>
        ) : readFailed ? (
          <Band
            kind="blocked"
            action={
              <Button variant="secondary" size="sm" loading={transmissions.isFetching} onClick={() => void transmissions.refetch()}>
                {copyText('retry')}
              </Button>
            }
          >
            <p>{copyText('transmissionsReadFailed')}</p>
          </Band>
        ) : (
          <div className="flex flex-col gap-1.5">
            <p
              id={id('delivered')}
              className="text-body text-navy-950"
              aria-invalid={deliveryError === undefined ? undefined : true}
              aria-describedby={deliveryError === undefined ? undefined : id('delivery-error')}
            >
              {copyText('deliveredByHand')}
            </p>
            {deliveryError !== undefined ? <FieldError id={id('delivery-error')} message={deliveryError} /> : null}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-start gap-2">
              <Checkbox
                id={id('consent')}
                checked={consent}
                onCheckedChange={(value) => setConsent(value === true)}
                aria-invalid={invalid('contact_consent')}
                aria-describedby={errorId('contact_consent')}
                className="mt-0.5"
              />
              <Label htmlFor={id('consent')} className="text-body font-normal">
                {PROVISIONAL_COPY.consentLabel.render({ email })}
              </Label>
            </div>
            {errors.contact_consent !== undefined ? (
              <FieldError id={id('contact_consent-error')} message={errors.contact_consent} />
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-start gap-2">
              <Checkbox
                id={id('altered')}
                checked={altered}
                onCheckedChange={(value) => setAltered(value === true)}
                aria-invalid={invalid('altered_after_download')}
                aria-describedby={errorId('altered_after_download')}
                className="mt-0.5"
              />
              <Label htmlFor={id('altered')} className="text-body font-normal">
                {copyText('alteredAfterDownloadLabel')}
              </Label>
            </div>
            {errors.altered_after_download !== undefined ? (
              <FieldError id={id('altered_after_download-error')} message={errors.altered_after_download} />
            ) : null}
          </div>
        </div>

        <p className="text-body-sm text-navy-700">{copyText('whatBloomWillNotDo')}</p>
      </DialogBody>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button
          type="submit"
          loading={submitting}
          {...(readFailed
            ? { disabled: true as const, disabledReason: copyText('transmissionsReadFailed') }
            : reportBureau === ''
              ? { disabled: true as const, disabledReason: copyText('chooseBureauFirst') }
              : {})}
        >
          Submit
        </Button>
      </DialogFooter>
    </form>
  )
}
